// src/storage/useEntitlements.js
//
// Auth-bound, READ-ONLY entitlement listener (Doc #2 section 3.4 Pattern A; WP-3).
// Subscribes to /users/{uid}/entitlements via onSnapshot. Entitlement is "active"
// iff status === 'active' AND expiresAt > now (locked #9). The listener is rebuilt
// whenever uid changes and torn down on unmount / sign-out (cleanup on uid change).
//
// Mock mode / unauthenticated / no-db: returns an empty, non-loading result so
// anonymous browse and mock dev work without Firebase (P7).
import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { useSchoolAccess } from './useSchoolAccess.js';

function expiresAtMillis(e) {
    const v = e && e.expiresAt;
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis(); // Firestore Timestamp
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
}

export function isEntitlementActive(e, now = Date.now()) {
    return !!(e && e.status === 'active' && expiresAtMillis(e) > now);
}

export function useEntitlements() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const school = useSchoolAccess(uid);
    const [entitlements, setEntitlements] = useState({}); // packId -> data
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // No backend in mock mode, or signed-out: empty + not loading.
        if (AUTH_MODE === 'mock' || !db || !uid) {
            setEntitlements({});
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const ref = collection(db, 'users', uid, 'entitlements');
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const map = {};
                snap.forEach((d) => { map[d.id] = d.data(); });
                setEntitlements(map);
                setLoading(false);
            },
            (err) => {
                // Read-only; do not retry/widen (C-X11 discipline). Surface in console.
                console.error('useEntitlements listener error:', err);
                setEntitlements({});
                setLoading(false);
            }
        );
        return () => unsub(); // cleanup on uid change / unmount
    }, [uid]);

    const now = Date.now();
    const combined = { ...entitlements };
    for (const id of school?.packIds || []) if (!isEntitlementActive(combined[id], now)) combined[id] = { status: 'active', source: 'school', packId: id, expiresAt: school.expiresAt };
    const activePackIds = new Set(
        Object.entries(combined)
            .filter(([, e]) => isEntitlementActive(e, now))
            .map(([packId]) => packId)
    );

    return { entitlements: combined, activePackIds, loading, uid };
}

export default useEntitlements;
